import { createSelector } from "@ngrx/store"
import { Performer } from "globals/store/performers.data"
import { Sponsor } from "globals/store/sponsors.data"
import { Attendee } from "globals/store/attendees.data"
import { ClipboardTab, Entity } from "globals/store/entity.data";
import { EntityName } from 'globals/store/entity.data';
import { jsonCopyObject, makeMapFromListOfObjectsAndKey, sortByProp } from "globals/helpers/helpers";
import { cleanClipboard, EntityClipboard } from "globals/store/accounts.data";
import { JoiAccount } from 'globals/store/accounts.data';
import { Dictionary } from "@ngrx/entity";
import { FilterGroup } from 'src/app/store/filters.era';
import { AssetsBaseTableData, AttendeeBaseTableData, CustomAnswerColumnData, customAnswerColumns, ExhibitorsBaseTableData, PerformerBaseTableData, ResourcesBaseTableData, SponsorsBaseTableData } from './../../helpers/table-column-types';
import { attendeeListReselector, exhibitorListReselector, getBaseAssetTableData, getBaseExhibitorTableData, getBasePerformerTableData, getBaseResourceTableData, getBaseAttendeeTableData, getBaseSponsorTableData, performerListReselector, resourceListReselector, sponsorListReselector } from "./entity.decircular.reselector";
import { JoiResource } from "globals/store/resources.data";
import { EntityBase, getCustomQuestionsByEntity, TypedEntity } from "globals/store/entity.shared.data";
import { Asset } from 'globals/store/assets.data';
import { CoreEntityType } from "globals/store/share.data";
import { CustomQuestion, getTypesAndQuestions, JoiEvent, Ranking } from "globals/store/events.data";
import { getEntityMediaTerms } from "globals/store/share.brief.functions";
import { TableColumn } from "src/app/helpers/table.data";
import { customQuestionColumnFromQuestion, tableTemplateIds, twoDeepSimpleSorter, twoDeepTextSorter } from "src/app/helpers/table-helpers";
import { capitalize, filterByKeywordAndProps } from 'src/app/helpers/helpers';
import { listsShareSomeElement } from 'globals/helpers/helpers';
import { JoiFile } from "globals/store/files.model";
import { MAX_ENTITY_STARS } from "globals/store/../helpers/constants";
import { InformableState, filterVisibleEntityInformableSections } from "globals/store/informables.data";
import { Exhibitor } from 'globals/store/exhibitors.data';
import { eventStateSelector } from "../events.era";
import { entitiesFeature } from "../entity.era";
import { accountStateSelector } from "../accounts.era";

const assets = (store)=> store.assets.entities
const cues = createSelector(eventStateSelector, (state) => state.event?.cues)
const clipboard = (store)=>store.accounts.account.clipboard
const accountEntities = createSelector(accountStateSelector, (state) => state.entities) // (store)=>store.accounts.entities
const templateTableFilters = (store) => store.filters.allTemplatesTable
const event = (store) => store.events.event
const typeIds = createSelector(entitiesFeature.selectTypeIds, (typeIds) => typeIds)
const informables = (store) => store.informables

export const entityTypesReselector = createSelector( // had to move here to avoid circular dependency
  event,
  (event: JoiEvent, type: CoreEntityType) => {
    if (!event) return null
    const { types, typesMap, questions, entityList } = getTypesAndQuestions(event, type )
    const questionCounts: Dictionary<number> = {}
    entityList.forEach(e => Object.keys(e.customQuestions).forEach(id => {
      if (!questionCounts[id]) questionCounts[id] = 0
      questionCounts[id] += 1
    }))
    return { types, typesMap, questionCounts, questions }

  }
)

export const mediaTermsReselector = createSelector( // had to move here to avoid circular dependency
  event,
  (event: JoiEvent, { type, entityTypeId }: { type: CoreEntityType, entityTypeId?: string }) => 
    !event || !type ? [] : event.mediaTerms[type]
)

export const entityMediaTermsReselector = createSelector( // had to move here to avoid circular dependency
  event,
  getEntityMediaTerms  
)

export const assetListReselector = createSelector( // had to move here to avoid circular dependency
  assets,
  (entities: Dictionary<Asset>) => Object.values(entities).sort(sortByProp('name')) 
)

export const cueListReselector = createSelector( // had to move here to avoid circular dependency
  cues,
  (cues) => (jsonCopyObject(cues) || []).sort(sortByProp('rank')) 
)

export const cueMapReselector = createSelector( // had to move here to avoid circular dependency
  cues,
  (cues) => makeMapFromListOfObjectsAndKey(cues || [], 'id')
)

export const entitiesReselector = createSelector(
  performerListReselector,
  sponsorListReselector,
  exhibitorListReselector,
  assetListReselector,
  resourceListReselector,
  attendeeListReselector,
  (p: Array<Performer>, s: Array<Sponsor>, e: Array<Exhibitor>, a: Array<Asset>, r: Array<JoiResource>, t: Array<Attendee>) => {
    const entities: { [id:string]: TypedEntity } = {}
    p.forEach(e => entities[e.id] = { ...e, type: 'performer' })
    s.forEach(e => entities[e.id] = { ...e, type: 'sponsor' })
    e.forEach(e => entities[e.id] = { ...e, type: 'exhibitor' })
    a.forEach(e => entities[e.id] = { ...e, type: 'asset' })
    r.forEach(e => entities[e.id] = { ...e, type: 'resource' })
    t.forEach(e => entities[e.id] = { ...e, type: 'attendee' })
    return entities
  }
)



export const entityInformablesBriefViewReselector = createSelector(
  event,
  informables,
  entitiesReselector,
  (event: JoiEvent, informables, entities: { [id:string]: TypedEntity }, { entityId, informableKey }: { entityId: string, informableKey: keyof InformableState }) => {
    if (!event) return []
    const entity = entities[entityId] as EntityBase
    const types = informableKey === 'performers' ? event.performerTypes : informableKey === 'resources' ? event.resourceTypes : informableKey === 'exhibitors' ? event.exhibitorTypes : event.sponsorTypes
    return filterVisibleEntityInformableSections(informables[informableKey].info, entity.entityTypeId, types || [])
  }
)

export const customQuestionsByEntityTypeReselector = createSelector( // had to move here to avoid circular dependency
  event,
  entitiesReselector,
  (event: JoiEvent, entities: { [id:string]: TypedEntity }, { type, id }: { type: CoreEntityType, id: string }) => {
    const entity = entities[id]
    if (!event || !entity) return null
    return getCustomQuestionsByEntity(event, (entity as EntityBase).entityTypeId, type)
  }
  
)


export const allAssociatedAssetsIdsReselector = createSelector(
  assetListReselector,
  entitiesReselector,
  (assets: Asset[], allEntities: { [id:string]: TypedEntity }) => {
    const associatedAssetIdByEntityId: Dictionary<string[]> = {}
    Object.keys(allEntities).forEach(id => associatedAssetIdByEntityId[id] = [])
    assets.filter(a => associatedAssetIdByEntityId[a.associatedId])
          .forEach(a => associatedAssetIdByEntityId[a.associatedId].push(a.id))
    return associatedAssetIdByEntityId
  }
)

export const associatedAssetsReselector = createSelector(
  allAssociatedAssetsIdsReselector,
  (associatedAssetIdsByEntityId, associatedId: string) => associatedAssetIdsByEntityId[associatedId] || []
)



const filterAndMakeList = (entities: Dictionary<Entity>, searchTerm: string) => {
  const entityList = Object.values(entities)
  return !entityList.length ? null : entityList.filter(filterByKeywordAndProps(searchTerm, ['eventName', 'name'] ))
}


export type ClipboardTableData = PerformerBaseTableData | ExhibitorsBaseTableData | SponsorsBaseTableData | AssetsBaseTableData | ResourcesBaseTableData | AttendeeBaseTableData
export const templateTableReselector = createSelector(
  accountEntities,
  templateTableFilters,
  (accounts: Dictionary<JoiAccount>, filters: FilterGroup, { accountId, type }: { accountId: string, type: ClipboardTab }) => {

    // console.log({ accounts })

    let account = accounts[accountId]
    if (!account) return null
    if (!account.clipboard) account = cleanClipboard(account) as JoiAccount
    let tableData: ClipboardTableData[]
    let entityList: Entity[]
    
    switch (type) {
      case 'ATTENDEES':
        entityList = filterAndMakeList(account.clipboard.attendees, filters.search)
        tableData = !entityList ? null : entityList.map(getBaseAttendeeTableData)
        break
      case 'PERFORMERS':
        entityList = filterAndMakeList(account.clipboard.performers, filters.search)
        tableData = !entityList ? null : entityList.map(getBasePerformerTableData)
        break
      case 'EXHIBITORS':
        entityList = filterAndMakeList(account.clipboard.exhibitors, filters.search)
        tableData = !entityList ? null : entityList.map(getBaseExhibitorTableData)
        break
      case 'SPONSORS':
        entityList = filterAndMakeList(account.clipboard.sponsors, filters.search)
        tableData = !entityList ? null : entityList.map(getBaseSponsorTableData)
        break
      case 'ASSETS':
        entityList = filterAndMakeList(account.clipboard.assets, filters.search)
        tableData = !entityList ? null : entityList.map(getBaseAssetTableData)
        break
      case 'RESOURCES':
        entityList = filterAndMakeList(account.clipboard.resources, filters.search)
        tableData = !entityList ? null : entityList.map(getBaseResourceTableData)
        break
      default:
        console.log("MISSING CLIPBOARD TYPE: " + type)
        break
    }

    const tableRowsById = !tableData ? null : makeMapFromListOfObjectsAndKey(tableData, 'id')
    // console.log({ tableData, tableRowsById, type })
    return { tableData, tableRowsById }
  }
)


export const assetTemplateReselector = createSelector(
  accountEntities,
  (accounts: Dictionary<JoiAccount>, { accountId, id }: { accountId: string, id: string }) => 
    accounts[accountId].clipboard.assets[id]
)

export const sponsorTemplateReselector = createSelector(
  accountEntities,
  (accounts: Dictionary<JoiAccount>, { accountId, id }: { accountId: string, id: string }) => 
    accounts[accountId].clipboard.sponsors[id]
)

export const exhibitorTemplateReselector = createSelector(
  accountEntities,
  (accounts: Dictionary<JoiAccount>, { accountId, id }: { accountId: string, id: string }) => 
    accounts[accountId].clipboard.exhibitors[id]
)

export const performerTemplateReselector = createSelector(
  accountEntities,
  (accounts: Dictionary<JoiAccount>, { accountId, id }: { accountId: string, id: string }) => 
    accounts[accountId].clipboard.performers[id]
)

export const attendeeTemplateReselector = createSelector(
  accountEntities,
  (accounts: Dictionary<JoiAccount>, { accountId, id }: { accountId: string, id: string }) => 
    accounts[accountId].clipboard.attendees[id]
)

export const resourceTemplateReselector = createSelector(
  accountEntities,
  (accounts: Dictionary<JoiAccount>, { accountId, id }: { accountId: string, id: string }) => 
    accounts[accountId].clipboard.resources[id]
)


export const templateListReselector = createSelector(
  clipboard,
  (clipboard: EntityClipboard, entityName: EntityName): Entity[] => {
    console.log({ clipboard })
    if (!clipboard) return []
    switch (entityName) {
      case 'Exhibitor':
        return Object.values(clipboard.exhibitors || {}).sort(sortByProp('name'))
      case 'Performer':
        return Object.values(clipboard.performers || {}).sort(sortByProp('name'))
      case 'Sponsor':
        return Object.values(clipboard.sponsors || {}).sort(sortByProp('name'))
      case 'Asset':
        return Object.values(clipboard.assets || {}).sort(sortByProp('name'))
      case 'Resource':
        return Object.values(clipboard.resources || {}).sort(sortByProp('name'))
      case 'Attendee':
        return Object.values(clipboard.attendees || {}).sort(sortByProp('name'))
      default:
        return []
    }
  }
)

export const createBaseCustomQuestionTableColumns = (entityType: CoreEntityType, forApp?: boolean): TableColumn[] => [{
  title: `${capitalize(entityType)} name`,
  id: customAnswerColumns.entityName,
  templateId: tableTemplateIds.boldText,
  sortingFunction: twoDeepTextSorter(customAnswerColumns.entityName, 'text'),
},{
  title: `${capitalize(entityType)} type`,
  id: customAnswerColumns.entityType,
  templateId: tableTemplateIds.tableText,
  sortingFunction: twoDeepTextSorter(customAnswerColumns.entityType, 'text'),
},{
  title: `Stars`,
  id: customAnswerColumns.stars,
  templateId: tableTemplateIds.tableStars,
  sortingFunction: twoDeepSimpleSorter(customAnswerColumns.stars, 'stars'),
}]

export const getFileAnswer = (answer: JoiFile[]): string => {
  if (!answer || !answer.length) return ''
  const { linkUrl } = answer[0]
  return linkUrl
}

export const customQuestionsTableReselector = createSelector(
  event,
  typeIds,
  (event: JoiEvent, typeIds: string[], entityType: CoreEntityType): CustomQuestionsTableReselectorData => {
    if (!event) return null
    let { typesMap, entityList: entities, questions } = getTypesAndQuestions(event, entityType)
    questions = questions.filter(q => !typeIds?.length || listsShareSomeElement(q.typeIds, typeIds))
    entities = entities.filter(e => !typeIds?.length || typeIds.includes(e.entityTypeId))
    const columns = createBaseCustomQuestionTableColumns(entityType)
    questions.filter(q=>q.type !== "text").map(customQuestionColumnFromQuestion).forEach((c: TableColumn) => columns.push(c))
  
    const items: CustomAnswerColumnData[] = entities.map(e => {
      const type = typesMap[e.entityTypeId]
      const item: CustomAnswerColumnData = {
        id: e.id,
        entityName: { text: e.name || `Unnamed ${entityType}`, bold: true },
        entityType: { text: !type ? 'Unknown type' : type.name || `Unnamed ${entityType} type` },
        stars: !e.stars ? null : { stars: e.stars, maxStars: MAX_ENTITY_STARS }
      }
      questions.forEach(q => {
        const notApplicable = q.typeIds.length && !q.typeIds.includes(e.entityTypeId)
        const answer = e.customQuestions[q.id]
        const fullyNotApplicable = notApplicable && !answer
        item[q.id] = q.type === 'link' ? { url: fullyNotApplicable ? 'N/A' : getFileAnswer(answer as JoiFile[]), NA: fullyNotApplicable } : 
          q.type === 'stars' ? (!answer ? null : { stars: answer, maxStars: MAX_ENTITY_STARS }) :
        { text: fullyNotApplicable ? 'N/A' :
          q.type === 'checkbox' ? (answer ? 'checked' : 'unchecked') :
          q.type === 'radio' ? answer || 'unselected' : answer || '' }
      })
      return item
    })

    return { columns, items, questions }
  }
)
export interface CustomQuestionsTableReselectorData {
  columns: TableColumn[]
  items: CustomAnswerColumnData[]
  questions: CustomQuestion[]
}